import { Checkbox, Form, InputNumber } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { getAdminPricingPlans, updateExistingPlan } from "../../../api/pricing";
import FormButton from "../../../component/common/FormButton";
import FormCheckbox from "../../../component/common/FormCheckbox";
import FormInput from "../../../component/common/FormInput";
import FormTextArea from "../../../component/common/FormTextArea";
import MultiSelect from "../../../component/common/MultiSelect";

const UpdatePricingPlan = ({ setPlans, setOpenModal, plan }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [resetTrigger, setResetTrigger] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const fetchPricingPlans = async () => {
    try {
      const res = await getAdminPricingPlans();
      if (res?.data?.plans) {
        const formattedPlans = res.data.plans.map(plan => ({
          ...plan,
          is_active: plan.status === "active",
        }));
        setPlans(formattedPlans);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (values.upload) {
        values.files = values.upload.fileList;
        delete values.upload;
        setResetTrigger(!resetTrigger);
      }
      await handleUpdatePlan(values);
      setTimeout(() => {
        form.resetFields();
      }, 1000);
    } catch (info) {
      console.log("Validate Failed:", info);
    }
  };

  const handleUpdatePlan = async (values) => {
    setLoading(true);

    try {
      const payload = {
        name: values.name,
        description: values.description,
        token: values.token,
        assistant_limit: values.assistant_limit,
        chat_providers: values.chat_providers,
        max_file: values.max_file,
        user_count: values.user_count,
        advance_parameter: values.advance_parameter,
        customize_chat: values.customize_chat,
        status: values.is_active ? "active" : "inactive",
      };

      const res = await updateExistingPlan(plan._id, payload);
      const plans = res?.data?.plans;
      if (plans) {
        setPlans(plans);
        toast.success(res?.data?.message);
        setOpenModal(false);
      };
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formItems = (selectedPlan) => [
    {
      name: "name",
      label: "Plan Name",
      type: "text",
      rules: [
        {
          required: true,
          message: "Pricing plan name is required!",
        },
      ],
    },
    {
      name: "description",
      label: "Description",
      type: "textArea",
      rows: 3,
      placeholder: "Write about the plan.",
      rules: [
        {
          required: true,
          message: "Pricing plan description is required!",
        },
      ],
    },
    {
      name: "price_amount",
      label: "Price",
      type: "number",
      disabled: true,
      rules: [
        {
          required: true,
          message: "Plan Price is required!",
        },
      ],
    },
    {
      name: "token",
      label: "Chat Token",
      type: "number",
      rules: [
        {
          required: true,
          message: "Chat token is required!",
        },
      ],
    },
    {
      name: "assistant_limit",
      label: "Assistant Limit",
      type: "number",
      rules: [
        {
          required: true,
          message: "Assistant limit is required!",
        },
      ],
    },
    {
      name: "chat_providers",
      label: "Chat Providers",
      type: "multiselect",
      options: ['openai', 'gemini', 'claude'].map((item) => ({
        label: item,
        value: item,
      })),
      rules: [
        {
          required: true,
          message: "At least one chat provider is required!",
        },
      ],
    },
    {
      name: "is_active",
      label: "Activate Plan",
      type: "checkbox",
      initialValue: selectedPlan?.status === "active",
    },
    {
      name: "max_file",
      label: "Max File Upload",
      type: "number",
      rules: [
        {
          required: true,
          message: "Maximum file upload is required!",
        },
      ],
    },
    {
      name: "user_count",
      label: "User Limit",
      type: "number",
      rules: [
        {
          required: true,
          message: "User limit is required!",
        },
      ],
    },
    {
      name: "advance_parameter",
      label: "Access to Advance Parameter Settings",
      type: "checkbox",
      rules: [
        {
          required: true,
          message: "Advance parameter option is required!",
        },
      ],
    },
    {
      name: "customize_chat",
      label: "Access to Customize Chat.",
      type: "checkbox",
      rules: [
        {
          required: true,
          message: "Customize chat option is required!",
        },
      ],
    },
    {
      name: "submitreset",
      submitlabel: "Update Plan",
      resetlabel: "Reset",
      type: "submitreset",
      className: "m-1",
      loading: loading
    },
  ];

  useEffect(() => {
    if (plan) {
      form.setFieldsValue(plan);
    }
  }, [plan, form]);

  const dynamicFormItems = formItems(selectedPlan);

  return (
    <>
      <h1
        className={`form-title ${localStorage.getItem("theme") === "dark" ? "text-white" : "text-black"}`}
      >
        Update Pricing Plan
      </h1>


      <Form form={form} autoComplete="off" layout="vertical" className="form">
        {dynamicFormItems.map((item, index) => {
          switch (item.type) {
            case "text":
              return <FormInput key={index} {...item} />;
            case "textArea":
              return <FormTextArea key={index} {...item} />;
            case "number":
              return (
                <Form.Item
                  key={index}
                  label={item.label}
                  name={item.name}
                  rules={item.rules}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder={item.placeholder}
                    disabled={item.disabled}
                  />
                </Form.Item>
              );
            case "multiselect":
              return <MultiSelect key={index} {...item} />;
            case "checkbox":
              return (
                <Form.Item key={index} name={item.name} valuePropName="checked">
                  <Checkbox>{item.label}</Checkbox>
                </Form.Item>
              );
            case "submitreset":
              return (
                <div className="d-flex justify-content-end gap-2" key={index}>
                  <FormButton label={item.resetlabel} onClick={() => form.resetFields()} />
                  <FormButton
                    label={item.submitlabel}
                    buttonType="primary"
                    loading={loading}
                    onClick={handleOk}
                  />
                </div>
              );
            default:
              return null;
          }
        })}
      </Form>
    </>
  );
};

export default UpdatePricingPlan;